import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './BecomeTeacher.css';
import { FaCheckCircle } from "react-icons/fa";
import PageBanner from '../components/PageBanner/PageBanner';
import { apiCall } from '../utils/api';
import { toast } from 'react-toastify';
import y3 from '../assets/images/y3.jpg';
import y4 from '../assets/images/y4.jpg';
import y5 from '../assets/images/y5.jpg';
import y6 from '../assets/images/y6.jpg';
import m1 from '../assets/images/m1.jpg';


function BecomeTeacher() {
  const { t } = useLanguage();

  // SCROLL REVEAL EFFECT
  useEffect(() => {
    const revealElements = document.querySelectorAll(".reveal");

    const revealOnScroll = () => {
      revealElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 80) {
          el.classList.add("visible");
        }
      });
    };

    window.addEventListener("scroll", revealOnScroll);
    revealOnScroll();

    return () => window.removeEventListener("scroll", revealOnScroll);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    certifications: '',
    certificateFile: null,
    certificateFileName: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);


  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[0-9]{10}$/.test(phone.replace(/\D/g, ''));

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = t('become_teacher.form.errors.name');

    if (!formData.email.trim()) newErrors.email = t('become_teacher.form.errors.email');
    else if (!validateEmail(formData.email)) newErrors.email = t('become_teacher.form.errors.email_invalid');

    if (!formData.phone.trim()) newErrors.phone = t('become_teacher.form.errors.phone');
    else if (!validatePhone(formData.phone)) newErrors.phone = t('become_teacher.form.errors.phone_invalid');

    if (!formData.experience.trim()) newErrors.experience = t('become_teacher.form.errors.experience');

    if (!formData.certificateFile) newErrors.certificateFile = t('become_teacher.form.errors.upload');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const formDataPayload = new FormData();
      formDataPayload.append('name', formData.name);
      formDataPayload.append('email', formData.email);
      formDataPayload.append('phone', formData.phone);
      formDataPayload.append('experience', formData.experience);
      formDataPayload.append('specialization', formData.certifications); // Mapping certs to specialization field in Schema
      if (formData.certificateFile) {
        formDataPayload.append('resume', formData.certificateFile); // Mapping to resume field
      }
      if (formData.message) {
        // If message is needed, schema doesn't have it. Let's ignore or add to schema.
        // Schema has 'specialization', 'experience'.
        // Let's assume message is not critical or map to something else.
      }

      try {
        await apiCall('/teacher-applications', 'POST', formDataPayload, true);

        setSubmitted(true);
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            experience: '',
            certifications: '',
            certificateFile: null,
            certificateFileName: '',
            message: ''
          });
          setSubmitted(false);
        }, 3500);
      } catch (error) {
        toast.error('Failed to submit application: ' + error.message);
      }
    }
  };


  // const requirements = [
  //   { icon: "🎓", title: "Certification Required", desc: "Yoga Alliance or equivalent qualification" },
  //   { icon: "👥", title: "Teaching Experience", desc: "Minimum 2 years practical experience" },
  //   { icon: "✨", title: "Passion & Energy", desc: "Genuine love for teaching and guiding students" },
  //   { icon: "🌱", title: "Growth Mindset", desc: "Willingness to learn and evolve continuously" }
  // ];
  const requirements = [
    {
      icon: y3,
      title: t('become_teacher.requirements.r1_title'),
      desc: t('become_teacher.requirements.r1_desc')
    },
    {
      icon: y4,
      title: t('become_teacher.requirements.r2_title'),
      desc: t('become_teacher.requirements.r2_desc')
    },
    {
      icon: y5,
      title: t('become_teacher.requirements.r3_title'),
      desc: t('become_teacher.requirements.r3_desc')
    },
    {
      icon: y6,
      title: t('become_teacher.requirements.r4_title'),
      desc: t('become_teacher.requirements.r4_desc')
    }
  ];


  const benefits = [
    t('become_teacher.benefits.l1'),
    t('become_teacher.benefits.l2'),
    t('become_teacher.benefits.l3'),
    t('become_teacher.benefits.l4'),
    t('become_teacher.benefits.l5'),
    t('become_teacher.benefits.l6')
  ];

  return (
    <div className="become-teacher">

      {/* Dynamic Page Banner from Admin */}
      <PageBanner
        pageKey="become-teacher"
        title={t('become_teacher.banner.title')}
        subtitle={t('become_teacher.banner.subtitle')}
      />

      {/* Hero Section */}
      <section className="teacher-hero reveal">
        <div className="container hero-container">
          <div className="herocontent">
            <h2>{t('become_teacher.hero.title')}</h2>
            <p>
              {t('become_teacher.hero.desc')}
            </p>

            <div className="hero-tags">
              <span className="hero-tag shimmer">{t('become_teacher.hero.tag1')}</span>
              <span className="hero-tag shimmer">{t('become_teacher.hero.tag2')}</span>
              <span className="hero-tag shimmer">{t('become_teacher.hero.tag3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="requirements-section reveal">
        <div className="container">
          <h2 className="section-title">{t('become_teacher.requirements.title')}</h2>

          <div className="requirements-grid">
            {requirements.map((req, index) => (
              <div key={index} className="requirement-card tilt-card">
                {/* <div className="req-icon">{req.icon}</div> */}
                <div className="req-icon">
                  <img
                    src={req.icon}
                    alt={req.title}
                    className="requirement-icon"
                  />
                </div>


                <h3>{req.title}</h3>
                <p>{req.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="teacher-benefits reveal">
        <div className="container benefits-wrapper">
          <div className="benefits-content">
            <h2 className="glow-title">{t('become_teacher.benefits.title')}</h2>

            <ul className="benefits-list">
              {benefits.map((benefit, index) => (
                <li key={index} className="benefit-item">✓ {benefit}</li>
              ))}
            </ul>

            <p className="benefits-text">
              {t('become_teacher.benefits.desc')}
            </p>
          </div>

          <div className="image-placeholder floating">
            <img
              src={m1}
              alt="Yoga Teacher"
              className="teacher-image"
            />
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="teacher-form-section reveal">
        <div className="container form-wrapper">

          <div className="form-container glass-effect">
            <h2 style={{ color: 'white' }}>{t('become_teacher.form.title')}</h2>

            {submitted ? (
              <div className="success-message scale-in">
                <div className="success-icon">
                  <FaCheckCircle />
                </div>
                <h3>{t('become_teacher.form.success_title')}</h3>
                <p>{t('become_teacher.form.success_desc')}</p>
              </div>
            ) : (
              <form className="teacher-form" onSubmit={handleSubmit}>

                {/* Name */}
                <div className="form-group">
                  <label>{t('become_teacher.form.name')}</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? "input-error" : ""}
                    placeholder={t('become_teacher.form.name_placeholder')}
                  />
                  {errors.name && <p className="error-message">{errors.name}</p>}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label>{t('become_teacher.form.email')}</label>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "input-error" : ""}
                    placeholder={t('become_teacher.form.email_placeholder')}
                  />
                  {errors.email && <p className="error-message">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div className="form-group">
                  <label>{t('become_teacher.form.phone')}</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? "input-error" : ""}
                    placeholder={t('become_teacher.form.phone_placeholder')}
                  />
                  {errors.phone && <p className="error-message">{errors.phone}</p>}
                </div>

                {/* Experience */}
                <div className="form-group">
                  <label>{t('become_teacher.form.experience')}</label>
                  <textarea
                    name="experience"
                    rows="4"
                    value={formData.experience}
                    onChange={handleChange}
                    className={errors.experience ? "input-error" : ""}
                    placeholder={t('become_teacher.form.experience_placeholder')}
                  />
                  {errors.experience && <p className="error-message">{errors.experience}</p>}
                </div>

                {/* Certifications */}
                <div className="form-group">
                  <label>{t('become_teacher.form.certifications')}</label>
                  <textarea
                    name="certifications"
                    rows="3"
                    value={formData.certifications}
                    onChange={handleChange}
                    placeholder={t('become_teacher.form.certifications_placeholder')}
                  />
                </div>

                {/* Certificate Upload */}
                <div className="form-group">
                  <label>{t('become_teacher.form.upload')}</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormData(prev => ({
                          ...prev,
                          certificateFile: file,
                          certificateFileName: file.name
                        }));
                      }
                    }}
                    className={errors.certificateFile ? "input-error" : ""}
                  />
                  {formData.certificateFileName && (
                    <p className="file-selected">
                      📄 {formData.certificateFileName}
                    </p>
                  )}
                  {errors.certificateFile && <p className="error-message">{errors.certificateFile}</p>}
                </div>

                {/* Extra Message */}
                <div className="form-group">
                  <label>{t('become_teacher.form.message')}</label>
                  <textarea
                    name="message"
                    rows="3"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('become_teacher.form.message_placeholder')}
                  />
                </div>

                {/* Submit Button */}
                <button className="submit-btn ripple" type="submit">
                  {t('become_teacher.form.btn')}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar Info Box */}
          <div className="info-box glass-effect">
            <h3>{t('become_teacher.sidebar.title')}</h3>
            <ul className="steps-list">
              <li>{t('become_teacher.sidebar.s1')}</li>
              <li>{t('become_teacher.sidebar.s2')}</li>
              <li>{t('become_teacher.sidebar.s3')}</li>
              <li>{t('become_teacher.sidebar.s4')}</li>
            </ul>

            <div className="contact-info">
              <h4>{t('become_teacher.sidebar.contact_title')}</h4>
              <p>📞 +91 98765 43210</p>
              <p>📧 teachers@yogakarega.com</p>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

export default BecomeTeacher;
